import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Receipt, CurrencyDollar, Calculator, Buildings, FileXls, CreditCard, Bank, Wallet } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import ExpensesPage from './ExpensesPage';
import EmployeeExpensesPage from './EmployeeExpensesPage';
import VATPage from './VATPage';
import ChargesPage from './ChargesPage';
import BankReconciliationPage from './BankReconciliationPage';
import PayrollVariablesPage from './PayrollVariablesPage';
import VerticalTabs from '../components/VerticalTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancePage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  // When the user navigates to /employee-expenses directly, land on the
  // "avances salariés" tab (admin) or "mes notes de frais" (employee) instead
  // of defaulting to the company-wide expenses ledger.
  const isEmpExpRoute = (typeof window !== 'undefined' && window.location.pathname.startsWith('/employee-expenses'));
  const initialAdminTab = isEmpExpRoute ? 'notes_frais' : 'depenses';
  const [mainTab, setMainTab] = useState(urlTab || (isAdmin ? initialAdminTab : 'notes_frais'));
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const { t } = useLanguage();

  useEffect(() => {
    if (urlTab && urlTab !== mainTab) setMainTab(urlTab);
  }, [urlTab, mainTab]);

  const handleTabChange = (t) => {
    setMainTab(t);
    setSearchParams({ tab: t }, { replace: true });
  };

  const handleExportGlobal = () => {
    window.open(`${API_URL}/api/finance/export/excel-global?month=${selectedMonth}`, '_blank');
  };

  // ---- Employee view : 2 sub-tabs (notes de frais + dépenses société) ----
  if (!isAdmin) {
    const EMP_TABS = [
      { key: 'notes_frais', label: 'Mes notes de frais', icon: CurrencyDollar },
      { key: 'depenses_societe', label: 'Achats société', icon: CreditCard },
    ];
    return (
      <div data-testid="finance-page">
        <div className="page-header">
          <h1 className="page-title">Mes dépenses</h1>
          <p className="page-subtitle">Notes de frais (à rembourser) et achats avec la carte société</p>
        </div>
        <VerticalTabs tabs={EMP_TABS} activeTab={mainTab === 'notes_frais' || mainTab === 'depenses_societe' ? mainTab : 'notes_frais'} onTabChange={handleTabChange}>
          {(mainTab === 'notes_frais' || mainTab !== 'depenses_societe') && (
            <EmployeeExpensesPage embedded paidBy="personal" />
          )}
          {mainTab === 'depenses_societe' && (
            <EmployeeExpensesPage embedded paidBy="company_card" />
          )}
        </VerticalTabs>
      </div>
    );
  }

  const TABS = [
    { key: 'depenses', label: t('tab.expenses'), icon: Receipt },
    { key: 'notes_frais', label: 'Avances salariés', icon: CurrencyDollar },
    { key: 'depenses_societe', label: 'Achats carte société', icon: CreditCard },
    { key: 'charges', label: 'Charges mensuelles', icon: Buildings },
    { key: 'tva', label: t('tab.vat'), icon: Calculator },
    { key: 'rapprochement', label: 'Rapprochement bancaire', icon: Bank },
    { key: 'paie', label: 'Variables de paie', icon: Wallet },
  ];

  return (
    <div data-testid="finance-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">{t('page.finance')}</h1>
          <p className="page-subtitle">{t('page.finance_sub')}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            data-testid="finance-export-month"
            className="form-input"
            style={{ height: 38 }}
          />
          <button
            type="button"
            onClick={handleExportGlobal}
            data-testid="finance-export-global-btn"
            className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Tous les sous-onglets en 1 fichier Excel"
          >
            <FileXls size={18} /> Export Excel global
          </button>
        </div>
      </div>

      <VerticalTabs tabs={TABS} activeTab={mainTab} onTabChange={handleTabChange}>
        {mainTab === 'depenses' && <ExpensesPage embedded />}
        {mainTab === 'notes_frais' && <EmployeeExpensesPage embedded paidBy="personal" />}
        {mainTab === 'depenses_societe' && <EmployeeExpensesPage embedded paidBy="company_card" />}
        {mainTab === 'charges' && <ChargesPage />}
        {mainTab === 'tva' && <VATPage embedded />}
        {mainTab === 'rapprochement' && <BankReconciliationPage />}
        {mainTab === 'paie' && <PayrollVariablesPage />}
      </VerticalTabs>
    </div>
  );
};

export default FinancePage;
